import { z } from 'zod';
import { INBOX_SORT_VALUES } from '../../../utils/inbox-filters';

/**
 * Partner featured entry for inbox listings
 * Tracks which partners have featured a listing
 */
export const PartnerFeaturedSchema = z.object({
  /** Partner display name */
  partnerName: z.string(),
  /** Partner code/identifier */
  partnerCode: z.string(),
  /** Membership type ID */
  membershipType: z.number().int().positive(),
  /** When the listing was featured for this partner */
  featuredDate: z.coerce.date(),
});

/**
 * Inbox listing detail schema
 * Extended listing data for admin inbox page
 */
export const InboxListingDetailSchema = z.object({
  /** Listing ID */
  id: z.number().int().positive(),
  /** Listing title */
  title: z.string(),
  /** Price as string for precision */
  price: z.string(),
  /** Original price before discount */
  original_price: z.number().nullable(),
  /** Category ID */
  categoryId: z.number().int().positive().nullable(),
  /** Category group ID */
  categoryGroupId: z.number().int().positive().nullable(),
  /** Has photo flag (0 or 1) */
  hasPhoto: z.number().int().min(0).max(1),
  /** Default photo index */
  defaultPhotoIndex: z.number().int().min(0),
  /** Item condition (1-5 scale) */
  itemCondition: z.number().int().nullable(),
  /** When listing was created */
  createdDate: z.coerce.date(),
  /** Listing status code */
  status: z.number().int().min(0),
  /** When listing was featured (if featured) */
  featuredDate: z.coerce.date().nullable(),
  /** Array of photo URLs */
  photos: z.array(z.string()),
  /** Partner feature entries */
  partnerFeatured: z.array(PartnerFeaturedSchema),
  /** Seller user ID */
  userId: z.number().int().positive().nullable(),
  /** Seller display name */
  userName: z.string().nullable(),
  /** Seller username */
  userUsername: z.string().nullable(),
  /** Seller email */
  userEmail: z.string().email().nullable(),
  /** Seller photo URL */
  userPhoto: z.string().nullable(),
  /** Seller trust level */
  userTrustLevel: z.number().int().min(0),
  /** Seller join date */
  userJoinDate: z.coerce.date().nullable(),
  /** Seller last active date */
  userLastActive: z.coerce.date().nullable(),
  /** Seller location text */
  userLocation: z.string().nullable(),
  /** Data source ID */
  dataSourceId: z.number().int().positive(),
  /** Data source name */
  dataSourceName: z.string(),
  /** Listing tags */
  tags: z.array(z.string()).optional(),
});

/**
 * Inbox API response schema
 */
export const InboxListingsResponseSchema = z.object({
  success: z.boolean(),
  listings: z.array(InboxListingDetailSchema),
  count: z.number().int().min(0),
});

/**
 * Sort options for inbox UI.
 *
 * Sourced from INBOX_SORT_OPTIONS, which is also what the Sort control renders —
 * this list was a second copy of it and had fallen four options behind.
 */
export const InboxSortOptionSchema = z.enum(INBOX_SORT_VALUES);

/**
 * Filter state for inbox UI
 */
export const InboxFilterStateSchema = z.object({
  selectedGroup: z.union([z.number().int().positive(), z.literal('all')]),
  selectedCategory: z.union([z.number().int().positive(), z.literal('all')]),
  selectedStatus: z.union([z.number().int().min(0), z.literal('all'), z.literal('featured')]),
  // 'vendoo' is not a data source id — see INBOX_SOURCE_OPTIONS.
  selectedDataSource: z.union([z.number().int().positive(), z.literal('all'), z.literal('vendoo')]),
  selectedPartner: z.union([z.number().int().positive(), z.literal('all')]),
  sortBy: InboxSortOptionSchema,
  searchTerm: z.string(),
  showSold: z.boolean(),
  showShippable: z.boolean(),
  showArchived: z.boolean(),
  fontSize: z.number().int().min(12).max(24),
});

// Type exports
export type PartnerFeatured = z.infer<typeof PartnerFeaturedSchema>;
export type InboxListingDetail = z.infer<typeof InboxListingDetailSchema>;
export type InboxListingsResponse = z.infer<typeof InboxListingsResponseSchema>;
export type InboxSortOption = z.infer<typeof InboxSortOptionSchema>;
export type InboxFilterState = z.infer<typeof InboxFilterStateSchema>;
